/**
 * [BLURY] | 🥭 Handles
 * The draggable shape handles laid over the canvas. Drag to move, alt-click to
 * delete, scroll to resize. The wheel listener is attached natively because
 * React's synthetic wheel handler is passive and cannot preventDefault.
 * @build 13.09.26
 * @updated 13.09.26 PHT
 * @author TONYTONY Sàrl
 */
import { useEffect, useRef, type PointerEvent as ReactPointerEvent, type RefObject } from 'react';
import { useStudio } from '@/state/useStudio';
import styles from './Handles.module.css';

const MIN_RADIUS = 0.03;
const MAX_RADIUS = 1.6;
const WHEEL_STEP = 0.0004;

interface HandlesProps {
  frameRef: RefObject<HTMLDivElement | null>;
}

export function Handles({ frameRef }: HandlesProps) {
  const shapes = useStudio(s => s.composition.shapes);
  const selected = useStudio(s => s.selected);
  const showHandles = useStudio(s => s.showHandles);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) {
      console.warn('Handles: no container found, skipping wheel binding');
      return;
    }
    const onWheel = (event: WheelEvent) => {
      const target = event.target as HTMLElement | null;
      const handle = target?.closest<HTMLElement>('[data-index]');
      if (!handle) return;
      event.preventDefault();

      const index = Number(handle.dataset.index);
      const { composition, updateShape, select } = useStudio.getState();
      const shape = composition.shapes[index];
      if (!shape) return;

      const r = Math.min(MAX_RADIUS, Math.max(MIN_RADIUS, shape.r - event.deltaY * WHEEL_STEP));
      updateShape(index, { r });
      select(index);
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  const startDrag = (event: ReactPointerEvent<HTMLButtonElement>, index: number) => {
    const { select, removeShape, updateShape } = useStudio.getState();

    if (event.altKey) {
      removeShape(index);
      return;
    }
    select(index);

    const frame = frameRef.current;
    if (!frame) {
      console.warn('Handles: no frame element found, skipping drag');
      return;
    }
    const rect = frame.getBoundingClientRect();
    event.currentTarget.setPointerCapture(event.pointerId);

    const move = (ev: PointerEvent) => {
      updateShape(index, {
        x: (ev.clientX - rect.left) / rect.width,
        y: (ev.clientY - rect.top) / rect.height,
      });
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  return (
    <div
      ref={containerRef}
      className={[styles.handles, showHandles ? '' : styles.hidden].join(' ')}
    >
      {shapes.map((shape, index) => (
        <button
          key={index}
          type="button"
          data-index={index}
          aria-label={`Shape ${index}`}
          className={[styles.handle, index === selected ? styles.selected : ''].join(' ')}
          style={{ left: `${shape.x * 100}%`, top: `${shape.y * 100}%` }}
          onPointerDown={event => startDrag(event, index)}
        >
          <span className={styles.index}>{index}</span>
        </button>
      ))}
    </div>
  );
}
